import React, { useState, useEffect } from 'react';
import { View, Text, Image, ScrollView, ActivityIndicator, StyleSheet, useWindowDimensions } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { CommonActions } from "@react-navigation/native";
import { createCheckout } from "../AdminPanel/Products/service/product-service";
import Button from "../../components/Button";
import THEME from "../../config/theme";
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { useCustomImages } from "../../api/Brand";
import { removeSansSerif } from "../../utils/validators";
import { hexToRgba } from "../../utils/colorUtils";

const formatPrice = (price) => {
  const value = Number(price);
  return Number.isFinite(value)
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;
};

// Mesmo texto de periodicidade do Checkout.
const getFrequencyText = (frequency) =>
  !frequency || String(frequency) === '1' ? 'por mês' : `a cada ${frequency} meses`;

// Processa a ASSINATURA (plan) com o cartão informado no Checkout. A compra única
// não passa por aqui: o Checkout redireciona direto para o link de pagamento.
const PostCheckoutProduct = ({ route, navigation }) => {
  const { data, productData } = route.params || {};
  // Ao recarregar a página os params se perdem (a URL guarda "[object Object]").
  const hasPurchase = !!productData && typeof productData === 'object' && !!data && typeof data === 'object';

  const [status, setStatus] = useState(hasPurchase ? 'processing' : 'error');
  const [error, setError] = useState(
    hasPurchase ? null : 'Os dados da compra não estão mais disponíveis. Volte ao checkout e preencha novamente.'
  );

  const { customColor, customFont } = useCustomTheme();
  const { data: logo } = useCustomImages('logoApp');
  const { width } = useWindowDimensions();
  const isNarrow = width < 480;

  useEffect(() => {
    if (!hasPurchase) return;
    createCheckout({ ...productData, ...data })
      .then(res => {
        if (res?.data?.status !== 200) {
          setError('Verifique os dados do cartão e tente novamente. Se o problema continuar, fale com o suporte.');
          setStatus('error');
        } else {
          setStatus('success');
        }
      })
      .catch(() => {
        setError('Ocorreu um erro inesperado. Tente novamente em instantes ou fale com o suporte.');
        setStatus('error');
      });
  }, [productData, data]);

  // ── Tokens da marca do cliente (mesmo padrão das telas do aluno) ──────────
  const clientBg = customColor?.background?.app || THEME.COLORS.BACKGROUND_MAIN;
  const clientText = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const clientPrimary = customColor?.primary || THEME.COLORS.PRIMARY_800;
  const clientBtnBg = customColor?.button?.backgroundApp || THEME.COLORS.PRIMARY_800;
  const clientBtnText = customColor?.button?.textApp || THEME.COLORS.TEXT_BUTTON;
  const cFontTitle = customFont?.fontFamilySubtitle
    ? removeSansSerif(customFont.fontFamilySubtitle)
    : THEME.FONTFAMILY.MEDIUM;
  const cFontText = customFont?.fontFamilyText
    ? removeSansSerif(customFont.fontFamilyText)
    : THEME.FONTFAMILY.REGULAR;
  const cSurface = hexToRgba(clientText, 0.05) || '#FFFFFF';
  const cSurfaceStrong = hexToRgba(clientText, 0.08) || '#F3F4F6';
  const cBorder = hexToRgba(clientText, 0.15) || '#E5E7EB';
  const cMuted = hexToRgba(clientText, 0.65) || '#6B7280';
  const accent = status === 'error' ? THEME.COLORS.ALERT : clientPrimary;
  const accentBg = hexToRgba(accent, 0.14) || cSurfaceStrong;

  const email = data?.customer?.email;
  const price = formatPrice(productData?.price);

  const content = {
    processing: {
      icon: <ActivityIndicator size="large" color={accent} />,
      title: 'Processando sua assinatura',
      message: 'Estamos confirmando o pagamento com a operadora do cartão. Isso leva alguns segundos, não feche esta página.',
    },
    success: {
      icon: <Feather name="check" size={34} color={accent} />,
      title: 'Assinatura confirmada!',
      message: email
        ? `Se esta é sua primeira compra, enviamos os dados de acesso para ${email}.`
        : 'Se esta é sua primeira compra, enviamos os dados de acesso para o seu e-mail.',
    },
    error: {
      icon: <Feather name="alert-circle" size={34} color={accent} />,
      title: 'Não foi possível concluir a assinatura',
      message: error,
    },
  }[status];

  const goToLogin = () =>
    navigation.dispatch(
      CommonActions.reset({
        index: 1,
        routes: [{ name: 'Login' }],
      })
    );

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: clientBg }}
      contentContainerStyle={[styles.scrollContent, { padding: isNarrow ? 16 : 32 }]}
    >
      {!!logo?.url && (
        <Image source={{ uri: logo.url }} style={styles.logo} resizeMode="contain" />
      )}

      <View
        style={[
          styles.card,
          { backgroundColor: cSurface, borderColor: cBorder, padding: isNarrow ? 24 : 36 },
        ]}
      >
        <View style={[styles.iconCircle, { backgroundColor: accentBg }]}>
          {content.icon}
        </View>

        <Text style={[styles.title, { color: clientText, fontFamily: cFontTitle, fontSize: isNarrow ? 20 : 22 }]}>
          {content.title}
        </Text>
        <Text style={[styles.message, { color: cMuted, fontFamily: cFontText }]}>
          {content.message}
        </Text>

        {hasPurchase && status !== 'error' && (
          <View style={[styles.summary, { backgroundColor: cSurfaceStrong, borderColor: cBorder }]}>
            <Text numberOfLines={2} style={[styles.summaryName, { color: clientText, fontFamily: cFontTitle }]}>
              {productData.name}
            </Text>
            {!!price && (
              <Text style={[styles.summaryPrice, { color: cMuted, fontFamily: cFontText }]}>
                {price} {getFrequencyText(productData.frequency)}
              </Text>
            )}
          </View>
        )}

        {status === 'success' && (
          <View style={styles.actions}>
            <Button
              title="Fazer login"
              onPress={goToLogin}
              width="100%"
              borderRadius="10px"
              colorbutton={clientBtnBg}
              colortitle={clientBtnText}
            />
          </View>
        )}

        {status === 'error' && navigation.canGoBack() && (
          <View style={styles.actions}>
            <Button
              title="Voltar e revisar dados"
              onPress={() => navigation.goBack()}
              width="100%"
              borderRadius="10px"
              colorbutton={clientBtnBg}
              colortitle={clientBtnText}
            />
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 180,
    height: 56,
    marginBottom: 24,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    borderWidth: 1,
    borderRadius: 16,
    alignItems: 'center',
    boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
  },
  iconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  title: {
    fontWeight: '700',
    textAlign: 'center',
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginTop: 8,
    maxWidth: 380,
  },
  summary: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginTop: 24,
    alignItems: 'center',
  },
  summaryName: {
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },
  summaryPrice: {
    fontSize: 14,
    marginTop: 4,
    textAlign: 'center',
  },
  actions: {
    width: '100%',
    marginTop: 24,
  },
});

export default PostCheckoutProduct;
